import { useCallback, useEffect, useState } from 'react';
import AmicaleValidationApi from '../../../api/AmicaleValidationApi';
import {
  clearAmicaleValidationSession,
  getAmicaleValidationAccessToken,
  getAmicaleValidationClient,
  getAmicaleValidationUser,
  persistAmicaleValidationSession,
} from './amicaleValidationStorage';

export const useAmicaleValidationAuth = () => {
  const [user, setUser] = useState(() => getAmicaleValidationUser());
  const [client, setClient] = useState(() => getAmicaleValidationClient());
  const [token, setToken] = useState(() => getAmicaleValidationAccessToken());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isAuthenticated = !!token && !!user;

  const login = useCallback(async ({ login: loginValue, password }) => {
    setLoading(true);
    setError(null);
    try {
      const data = await AmicaleValidationApi.login({
        login: loginValue,
        password,
      });
      persistAmicaleValidationSession({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        user: data.user,
        client: data.client,
      });
      setUser(data.user);
      setClient(data.client);
      setToken(data.accessToken);
      return { success: true };
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Erreur de connexion';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    clearAmicaleValidationSession();
    setUser(null);
    setClient(null);
    setToken(null);
  }, []);

  // Refresh `me` on mount when a token exists
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await AmicaleValidationApi.getMe();
        if (!cancelled && data?.client) setClient(data.client);
      } catch {
        if (!cancelled) logout();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, logout]);

  return {
    user,
    client,
    token,
    loading,
    error,
    isAuthenticated,
    login,
    logout,
  };
};

export default useAmicaleValidationAuth;
